import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { Input } from '@/components/ui/input'
import { Plus, Search, MoreHorizontal } from 'lucide-react'
import { ConfirmationModal } from '@/components/ConfirmationModal'
import { useSortedChatbots, useUpdateChatbotStatus } from '@/lib/queries'
import { formatDateTime } from '@/lib/utils'

const getShortDescription = (html: string | undefined | null) => {
    if (!html) return "-";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    const plainText = tmp.textContent || tmp.innerText || "";
    return plainText.length > 50 ? plainText.substring(0, 50) + "..." : plainText;
};

export function Dashboard() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: chatbots = [], isLoading } = useSortedChatbots()
    const updateStatusMutation = useUpdateChatbotStatus()
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState("")
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'Activate' | 'Deactivate' | null;
        botId: string | null;
    }>({ isOpen: false, type: null, botId: null })
    const isFaculty = user?.division === 'teacher'
    const [pageInput, setPageInput] = useState("1")
    const [pageInputError, setPageInputError] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm])

    useEffect(() => {
        setPageInput(currentPage.toString())
        setPageInputError(false)
    }, [currentPage])

    const handleStatusAction = (botId: string, currentStatus: string) => {
        const type = currentStatus === 'Active' ? 'Deactivate' : 'Activate'
        setModalConfig({ isOpen: true, type, botId })
        setOpenMenuId(null)
    }

    const confirmStatusChange = async () => {
        if (modalConfig.botId && modalConfig.type) {
            const newStatus = modalConfig.type === 'Activate' ? 'Active' : 'Inactive'
            updateStatusMutation.mutate(
                { id: modalConfig.botId, status: newStatus },
                { onSettled: () => setModalConfig({ isOpen: false, type: null, botId: null }) }
            )
        }
    }

    const filteredChatbots = chatbots.filter(bot =>
        bot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bot.id.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const itemsPerPage = 10
    const totalPages = Math.ceil(filteredChatbots.length / itemsPerPage)
    const currentItems = filteredChatbots.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

    return (
        <>
            <div className="p-8 h-full">
                <h1 className="text-3xl font-bold text-black mb-8">Chatbot Management</h1>

                <div className="mb-6 flex items-center justify-between">
                    <div className="relative w-[450px]">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <Input
                            placeholder="Search by Chatbot name or Chatbot ID"
                            className="pl-10 h-10 border-gray-300"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Link to="/chatbot" search={{ mode: 'create' }}>
                        <Button className="gap-2 bg-[#D91F27] hover:bg-[#b81a21] text-white font-medium px-6">
                            <Plus size={18} />
                            Create New Chatbot
                        </Button>
                    </Link>
                </div>

                <div className="bg-white border border-gray-200 rounded-md overflow-visible shadow-sm">
                    <Table>
                        <TableHeader className="bg-[#D91F27]">
                            <TableRow className="hover:bg-[#D91F27] border-none">
                                <TableHead className="text-white font-medium h-12">ID</TableHead>
                                <TableHead className="text-white font-medium">Name</TableHead>
                                <TableHead className="text-white font-medium">Description</TableHead>
                                <TableHead className="text-white font-medium">Publish Date</TableHead>
                                <TableHead className="text-white font-medium">Created At</TableHead>
                                <TableHead className="text-white font-medium">Last Modified</TableHead>
                                <TableHead className="text-white font-medium">Status</TableHead>
                                <TableHead className="text-white font-medium text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentItems.map((bot) => (
                                <TableRow key={bot.id} className="hover:bg-gray-50">
                                    <TableCell className="font-medium text-gray-900">{bot.id}</TableCell>
                                    <TableCell className="text-gray-900">
                                        <span
                                            className="cursor-pointer hover:text-[#D91F27] hover:underline"
                                            onClick={() => navigate({ to: '/chatbot', search: { mode: 'edit', id: bot.id } })}
                                        >
                                            {bot.name}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-gray-600">
                                        <div className="group relative">
                                            <div className="line-clamp-2 cursor-pointer">
                                                {getShortDescription(bot.description)}
                                            </div>
                                            {bot.description && (
                                                <div className="absolute left-0 bottom-full pb-2 hidden group-hover:block z-[50]">
                                                    <div className="w-max max-w-[400px] p-4 bg-white border border-gray-200 shadow-xl rounded-lg text-sm max-h-[300px] overflow-y-auto cursor-auto">
                                                        <div
                                                            dangerouslySetInnerHTML={{ __html: bot.description }}
                                                            className="[&_b]:font-bold [&_i]:italic [&_u]:underline [&_a]:text-blue-600 [&_a]:underline whitespace-pre-wrap text-gray-900"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-gray-900">
                                        {bot.publishDate ? formatDateTime(bot.publishDate) : '-'}
                                    </TableCell>
                                    <TableCell className="text-gray-900">{formatDateTime(bot.createdAt)}</TableCell>
                                    <TableCell className="text-gray-900">{formatDateTime(bot.lastModified)}</TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-flex items-center px-3 py-1 rounded text-xs font-semibold text-white ${bot.status === 'Active'
                                                ? 'bg-[#198754]'
                                                : 'bg-gray-400'
                                                }`}
                                        >
                                            {bot.status}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right relative">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setOpenMenuId(openMenuId === bot.id ? null : bot.id)
                                            }}
                                        >
                                            <MoreHorizontal size={18} className="text-gray-500" />
                                        </Button>
                                        {openMenuId === bot.id && (
                                            <div
                                                ref={menuRef}
                                                className="absolute right-8 top-0 z-10 w-32 bg-white rounded-md shadow-lg border border-gray-200 py-1"
                                            >
                                                <button
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => navigate({ to: '/chatbot', search: { mode: 'edit', id: bot.id } })}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => {
                                                        handleStatusAction(bot.id, bot.status)
                                                    }}
                                                >
                                                    {bot.status === 'Active' ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-center px-4 py-3 gap-3">
                    <Button variant="outline" size="sm" className="h-8 px-3 text-sm font-medium bg-white" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                        Previous
                    </Button>

                    <div className="flex items-center gap-2">
                        {Array.from({ length: totalPages || 1 }).map((_, i) => (
                            <div key={i} className={`h-2 rounded-full transition-all duration-300 ${currentPage === i + 1 ? 'w-4 bg-[#D91F27]' : 'w-2 bg-rose-200'}`}></div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 mx-2">
                        <Input
                            className="w-12 h-8 text-center px-1"
                            aria-invalid={pageInputError}
                            value={pageInput}
                            onChange={(e) => {
                                const valStr = e.target.value
                                setPageInput(valStr)
                                const val = parseInt(valStr)
                                if (valStr === "") {
                                    setPageInputError(true)
                                } else if (!isNaN(val) && val >= 1 && val <= (totalPages || 1)) {
                                    setPageInputError(false)
                                    setCurrentPage(val)
                                } else {
                                    setPageInputError(true)
                                }
                            }}
                        />
                        <span className="text-sm text-gray-500 font-medium">of {totalPages || 1}</span>
                    </div>

                    <Button variant="outline" size="sm" className="h-8 px-3 text-sm font-medium bg-white" disabled={currentPage >= (totalPages || 1)} onClick={() => setCurrentPage(p => p + 1)}>
                        Next
                    </Button>
                </div>
            </div>
            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
                onConfirm={confirmStatusChange}
                title={`${modalConfig.type} Chatbot?`}
                description={modalConfig.type === 'Activate'
                    ? `The chatbot will be activated on the ${isFaculty ? 'faculty' : 'student'} site. Reload the site to view it.`
                    : `${isFaculty ? 'Faculty' : 'Students'} will no longer see this chatbot on the ${isFaculty ? 'faculty' : 'student'} site.`}
                confirmText={modalConfig.type || 'Confirm'}
                confirmVariant={modalConfig.type === 'Deactivate' ? 'destructive' : 'primary'}
                isLoading={updateStatusMutation.isPending}
            />
        </>
    )
}
